import classnames from "classnames";
import { TFunction } from "i18next";
import { inject, observer } from "mobx-react";
import React, { Component, Fragment } from "react";
import ReactDOM from "react-dom";
import { Translation } from "react-i18next";
import type { AuthEvent, ContestEvent, Store } from "../../../client/stores";
import { HamburgerIcon } from "../components/icons/HamburgerIcon.component";
import { SocialIcons } from "../components/icons/SocialIcons.component";
import { Microsite, Site } from "../decoders/Sites.types";
import type { SitesMenu as DataMenu } from "../generated/webapi.g";
import * as Webapi from "../generated/webapi.g";
import { abTranslator } from "../lib/abHelper";
import { bodyScroll, getLiveStationUrl, slugify } from "../lib/common";
import { logger } from "../lib/logging";
import { hasPage, reverseRoute } from "../lib/route";
import { socialConfigAbstract, SocialNetworkData } from "../lib/social";
import { getContests } from "../services/Aptivada";
import { CloseButton, MagicLink, SearchBox } from "../ui";
import "./Navigation.style.scss";
import { NavigationMenu } from "./NavigationMenu.component";
import { StationLogo } from "./StationLogo.component";
import { UserProfile } from "./UserProfile.component";

const log = logger.getChild("Navigation.component");

export interface SitesMenu extends DataMenu {
    forceOpen?: boolean;
    href?: string;
    resolved?: boolean;
    context?: string;
    children?: SitesMenu[] | null;
}

interface NavProps {
    menus?: SitesMenu[] | null;
    sections: Webapi.UserConfigFragment;
    store?: Store;
    social?: SocialNetworkData[];
    open?: boolean;
    t: TFunction;
}

interface NavState {
    open: boolean;
    resolvedMenus?: SitesMenu[];
}

interface NavContentProps extends Omit<NavProps, "t"> {
    closeNav: () => void;
}

export function resolveMenu(
    menu: SitesMenu,
    micrositeRoot = "/featured/",
    site?: Site | Microsite,
    brandIds?: { [key: string]: string },
) {
    if (!site || menu.href) {
        return menu;
    }

    if (menu.type === "brand" && menu.ref) {
        if (!brandIds?.[`/index${menu.ref}`]) {
            logger.warn("No brand found for id: ", menu.ref);
            return menu;
        }
        menu.href = `${micrositeRoot}${brandIds?.[`/index${menu.ref}`]}/`;
    } else if (menu.type === "page") {
        if (menu.ref) {
            menu.href = reverseRoute(site, menu.ref);
        }
    } else if (menu.ref && !menu.href) {
        menu.href = menu.ref;
    }

    if (menu.children) {
        menu.children = menu.children.map(m => resolveMenu(m, micrositeRoot, site, brandIds));
    }

    return menu;
}

const phoneNumber = "1-844-BUY-RADIO";
const NavContent = inject("store")(({ open, menus, store, social, sections, closeNav }: NavContentProps) => {
    if (!store) {
        return null;
    }
    const navClass = classnames({
        open,
    });

    const liveUrl = getLiveStationUrl(store.site, store.env);
    return (
        <React.Fragment>
            <nav
                className={navClass}
                tabIndex={open ? 0 : -1}
                aria-hidden={!open}
                aria-label="Primary Site Navigation"
                id="component-site-nav"
            >
                <section className="nav-inner-wrapper">
                    <header className="navbar-header">
                        <StationLogo />
                        <CloseButton click={closeNav} label="Close Site Navigation" />
                    </header>
                    <hr className="divider" />
                    <ul className="menu-container main-nav">
                        {liveUrl && sections.partners?.iheartradio_switch ? (
                            <li className="menu-top-label">
                                <Translation>
                                    {t => (
                                        <MagicLink to={liveUrl} context="listen">
                                            {t("listen")}
                                        </MagicLink>
                                    )}
                                </Translation>
                            </li>
                        ) : null}
                        {menus &&
                            menus.map((menu, i) => (
                                <NavigationMenu
                                    key={i}
                                    mainNavOpen={open}
                                    forceOpen={menu.forceOpen}
                                    menu={menu}
                                    store={store}
                                />
                            ))}
                        {sections.navigation?.contact_menu_switch ? (
                            <li className="menu-top-label">
                                <Translation>
                                    {t => (
                                        <MagicLink to={reverseRoute(store.site, "contact") || ""} context="contact">
                                            {t("contact")}
                                        </MagicLink>
                                    )}
                                </Translation>
                            </li>
                        ) : null}
                        {sections.partners?.postup_switch ? (
                            <li className="menu-top-label">
                                <Translation>
                                    {t => (
                                        <MagicLink
                                            to={reverseRoute(store.site, "newsletter") || ""}
                                            context="newsletter"
                                        >
                                            {t("newsletter")}
                                        </MagicLink>
                                    )}
                                </Translation>
                            </li>
                        ) : null}
                        {sections.navigation?.advertise_switch ? (
                            <li className="menu-top-label">
                                <Translation>
                                    {t => (
                                        <MagicLink to={reverseRoute(store.site, "advertise") || ""} context="advertise">
                                            {t("advertise_with_us", {
                                                name: store.site.sections.general?.name,
                                            })}
                                        </MagicLink>
                                    )}
                                </Translation>
                            </li>
                        ) : null}
                        {sections.navigation?.advertise_switch ? (
                            <li className="menu-top-label">
                                <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                            </li>
                        ) : null}
                    </ul>
                    {social ? <SocialIcons networks={social} /> : null}
                    <footer className="navbar-footer">
                        {sections.navigation?.search_switch ? <SearchBox location="nav" scrollIntoView={true} /> : null}
                        <UserProfile />
                    </footer>
                </section>
            </nav>
            {open ? <div className="scroll-blocker" onClick={closeNav} /> : null}
        </React.Fragment>
    );
});

@inject("store")
@observer
export class Navigation extends Component<NavProps, NavState> {
    private brandIds: { [key: string]: string } = {};
    private socialIcons: SocialNetworkData[] = [];
    mounted = false;

    constructor(props: NavProps) {
        super(props);

        if (!props.menus) {
            log.error("No Menu!");
        }

        if (props.store) {
            props.store.site.micrositeReferences.forEach(index => (this.brandIds[`/index${index._id}`] = index.slug));
        } else {
            log.error("No linked indexes!");
        }

        if (props.social) {
            this.socialIcons = props.social;
        } else if (props.sections.social) {
            this.socialIcons = socialConfigAbstract(props.sections.social);
        }

        this.state = {
            open: !!props.open,
            resolvedMenus: props.menus?.map(menu =>
                resolveMenu(menu, this.props.store?.env.MICROSITE_ROOT, this.props.store?.site, this.brandIds),
            ),
        };
    }

    componentDidMount() {
        this.mounted = true;
        this.props.store?.history?.listen(this.closeNav);
        this.props?.store?.onAuthAction?.subscribe((event: AuthEvent) => {
            if (event.action === "pending") {
                this.closeNav();
            }
        });

        this.props?.store?.onContestAction?.subscribe((event: ContestEvent) => {
            if (event.action === "contest_open") {
                this.closeNav();
            }
        });

        if (typeof window !== "undefined") {
            this.componentDidUpdate();
            this.loadContests();
        }
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    componentDidUpdate() {
        bodyScroll(!this.state.open, "nav");
    }

    toggleOpen = (open: boolean) => {
        if (this.mounted) {
            if (open) {
                const page = this.props.store?.page.currentPage;
                if (page) {
                    const pageName = page ? `${this.props.store?.microsite ? "microsite_" : ""}${page.name}` : "";
                    let referrer = "";
                    if (typeof window !== "undefined") {
                        referrer = window.location.href;
                    }

                    this.props.store?.onAnalyticsAction.dispatch({
                        sectionName: "nav",
                        pageName,
                        context: "hamburger_menu",
                        action: "click",
                        url: "",
                        referrer,
                    });
                }
            }

            this.setState({ open });
        }
    };

    openNav = () => this.toggleOpen(true);

    closeNav = () => this.toggleOpen(false);

    async loadContests() {
        if (this.props.store && this.props.store.site.sections.navigation?.contest_nav_switch) {
            const contests = await getContests(
                { accountId: this.props.store.site.index.slug },
                this.props.store.tags.surrogateKeys,
            );

            if (Array.isArray(contests)) {
                this.addContests(contests.slice(0, 5));
            } else {
                log.error("Error Loading Contests");
            }
        }
    }

    addContests = (contests: Webapi.AptivadaAppFragment[]) => {
        if (!this.props.store || this.props.store.site.sections.partners?.aptivada_switch !== true) {
            log.debug("Aptivada is disabled on the partners tab for this site");
            return;
        }
        const { resolvedMenus } = this.state;

        if (!resolvedMenus) {
            log.debug("No menu");
            return;
        }

        const hasPromotions = hasPage(this.props.store.site, "promotions");
        const hasPromotion = hasPage(this.props.store.site, "promotion");
        let contestsMenuIndex = resolvedMenus.findIndex(m => (m.label ? m.label.toLowerCase() === "contests" : false));
        contestsMenuIndex = contestsMenuIndex > -1 ? contestsMenuIndex : resolvedMenus.length;
        if (!resolvedMenus[contestsMenuIndex]) {
            const menuLabel = hasPromotions
                ? abTranslator("promotions", this.props.store?.testGroups, this.props.t!)
                : this.props.t("contests");
            resolvedMenus.push({
                type: "link",
                label: menuLabel,
                children: [],
                ref: "#",
            });
        }
        for (let i = contests.length - 1; i >= 0; i--) {
            const contest = contests[i];
            const menuItem: SitesMenu = {
                type: "link",
                label: contest.title.trim(),
                href: this.props.store.site
                    ? contest.gridRedirect ||
                      reverseRoute(this.props.store.site, hasPromotion ? "promotion" : "contest", {
                          slug: `${slugify(contest.title.trim(), 6)}-${contest.appId}`,
                      })
                    : "#",
                context: `contest_${i}`,
            };
            const { children } = resolvedMenus[contestsMenuIndex];
            if (children) {
                children.unshift(menuItem);
            }
        }
        const { children: contestsMenu } = resolvedMenus[contestsMenuIndex];
        if (contestsMenu) {
            const allContestLabel = hasPromotions
                ? abTranslator("all_promotions", this.props.store?.testGroups, this.props.t!)
                : this.props.t("all_contests");
            const contestRuleLabel = this.props.t("contest_rules");
            contestsMenu.push({
                type: "link",
                label: allContestLabel,
                href: reverseRoute(this.props.store.site, hasPromotions ? "promotions" : "contests"),
            });
            contestsMenu.push({
                type: "link",
                label: contestRuleLabel,
                href: reverseRoute(this.props.store.site, "rules"),
            });
        }

        if (this.mounted) {
            this.setState({ resolvedMenus });
        }
    };

    Nav = () => {
        if (typeof window === "undefined" || typeof process !== "undefined") {
            return (
                <NavContent
                    open={this.state.open}
                    menus={this.state.resolvedMenus}
                    social={this.socialIcons}
                    closeNav={() => this.toggleOpen(false)}
                    sections={this.props.sections}
                />
            );
        }
        return ReactDOM.createPortal(
            <NavContent
                open={this.state.open}
                menus={this.state.resolvedMenus}
                social={this.socialIcons}
                closeNav={() => this.toggleOpen(false)}
                sections={this.props.sections}
            />,
            document.body,
        );
    };

    render() {
        if (!this.props.store) {
            log.error("No Store Available!");
            return null;
        }

        return (
            <Fragment>
                <button
                    aria-label="Open Site Navigation"
                    className="nav-toggler"
                    onClick={() => this.toggleOpen(!this.state.open)}
                    tabIndex={0}
                >
                    <HamburgerIcon />
                </button>
                {this.Nav()}
            </Fragment>
        );
    }
}
